/**
 * Every `localStorage` key the add-in reads or writes.
 *
 * Identities, like the workbook table and cache names: a key written under one spelling and read
 * under another returns `null` with no error — the user is silently signed out, or a saved choice
 * quietly stops sticking. `browserStore` takes this union rather than `string` so a typo cannot
 * compile.
 *
 * Storage is shared with every other page on the origin, which is why each key is prefixed by the
 * thing that owns it rather than named for what it holds.
 */
export const STORAGE_KEYS = [
  /* Ediphi session */
  "xAuthToken",
  "idToken",
  "userProfile",
  "userEmail",
  "tenant",
  /* UI state */
  "activePage",
  "procoreExportSortFields",
  /* Togal session */
  "togalSessionId",
  "togalOrganizationId",
  "togalUserProfile",
  "togalLoginUsername",
] as const;

export type StorageKey = (typeof STORAGE_KEYS)[number];

/** Typed `localStorage`. Reads return `null` when absent, exactly as the raw API does. */
export const browserStore = {
  get: (key: StorageKey): string | null => localStorage.getItem(key),
  set: (key: StorageKey, value: string): void => localStorage.setItem(key, value),
  remove: (key: StorageKey): void => localStorage.removeItem(key),
};

/**
 * The `sessionStorage` keys that name what the workbook has loaded, one per datasource, so every
 * log and analytics event can say it without an async Excel read. Each is written and cleared with
 * its `.metadata` row, so it always names what the row names.
 */
export const LOADED_DATASOURCE_KEYS = [
  "selectedProjectId",
  "selectedEstimateId",
  "selectedGcgrId",
  "selectedCostModelId",
  "contractingLoaded",
] as const;

export type LoadedDatasourceKey = (typeof LOADED_DATASOURCE_KEYS)[number];

/** Typed `sessionStorage` for the loaded datasource keys. */
export const loadedDatasourceStore = {
  get: (key: LoadedDatasourceKey): string | null => sessionStorage.getItem(key),
  set: (key: LoadedDatasourceKey, value: string): void => sessionStorage.setItem(key, value),
  remove: (key: LoadedDatasourceKey): void => sessionStorage.removeItem(key),
  clear: (): void => LOADED_DATASOURCE_KEYS.forEach((key) => sessionStorage.removeItem(key)),
};
